/* eslint-disable sonarjs/no-duplicate-string */

import { LitElement } from "lit";
import { state } from "lit/decorators.js";
import type { ParamValue, RouterLocation } from "@vaadin/router";

import { languageClient } from "../services/language-service";
import {
  getStore,
  getPayorAndLocale,
  getSearch,
} from "../utilities/navigation.utility";

export abstract class WebKioskPageElement extends LitElement {
  @state() content: unknown = "not set";

  @state() location: RouterLocation | undefined = undefined;

  getTitle(): string {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (this.content?.title as string) || "Walgreens Health Corner";
  }

  getStoreId(): string {
    if (!this.location) {
      // eslint-disable-next-line no-console
      console.error("Location is undefined.");
      return "";
    }

    const searchParameters = new URLSearchParams(this.location.search);

    if (!searchParameters.has("store")) {
      return "";
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return searchParameters.get("store")!.replace("s", "");
  }

  getPayor(): string {
    if (!this.location) {
      // eslint-disable-next-line no-console
      console.error("Location is undefined.");
      return "";
    }

    if (this.location.params.payor === undefined) {
      // eslint-disable-next-line no-console
      console.error("Payor is undefined.");
      return "";
    }

    return this.location.params.payor.toString();
  }

  getLocale(): string {
    if (!this.location) {
      // eslint-disable-next-line no-console
      console.error("Location is undefined.");
      return "";
    }

    if (this.location.params.locale === undefined) {
      // eslint-disable-next-line no-console
      console.error("Locale is undefined.");
      return "";
    }

    return this.location.params.locale.toString();
  }

  createRelativeLink(route: string): string {
    if (!this.location) {
      return "";
    }

    const parameters = getPayorAndLocale(this.location);
    const search = getSearch(this.location);
    const store = getStore(search);

    return `${parameters.payor}/${parameters.locale}/${route}${store}`;
  }

  setTitle(title: string): void {
    window.document.title = title;
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.setTitle(this.getTitle());
  }

  onBeforeEnter() {
    this.updateContentFromUrl();
  }

  updateContentFromUrl() {
    if (!this.location) {
      return;
    }

    const parameters = getPayorAndLocale(this.location);
    const contentParameters = {
      ...this.location.params,

      ...Object.fromEntries(
        new URLSearchParams(window.location.search).entries()
      ),
    };

    this.content = this.loadContentEntry(
      parameters.locale,
      parameters.payor,
      contentParameters.contentKey
    );

    this.requestUpdate();
  }

  loadContentEntry(
    locale: string,
    payor: string,
    contentKey: ParamValue | undefined
  ): unknown {
    return languageClient.fetchContentEntry(locale, payor, contentKey);
  }
}
